<template>
  <div class="text-display">
    <div class="text-content markdown-body" v-html="renderedContent"></div>
  </div>
</template>

<script>
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import 'github-markdown-css';

// 配置 marked 选项
const renderer = new marked.Renderer();
marked.setOptions({
  renderer,
  gfm: true,
  breaks: true,
  headerIds: true,
  langPrefix: 'language-'
});

export default {
  name: 'TextDisplay',
  props: {
    text: {
      type: String,
      default: ''
    },
    audioId: {
      type: String,
      required: true
    },
    filePath: {
      type: String,
      required: true
    },
    duration: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      isGenerating: false
    };
  },
  computed: {
    renderedContent() {
      if (!this.text) return '';
      try {
        // 使用 DOMPurify 清理 HTML 以防止 XSS 攻击
        return DOMPurify.sanitize(marked.parse(this.text));
      } catch (error) {
        return this.text;
      }
    }
  }
}
</script>

<style scoped>
.text-display {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 0;
  height: 100%;
}

.text-content {
  flex: 1;
  overflow-y: auto;
  max-width: none;
  width: auto;
  margin: 0;
  padding: var(--space-6) max(var(--space-6), calc((100% - 760px) / 2)) 10rem;
  min-height: 0;
  box-sizing: border-box;
}

/* Markdown 样式 */
.markdown-body {
  font-family: var(--font-family-base);
  font-size: 1.02rem;
  line-height: 1.78;
  color: var(--ink-primary);
  background-color: transparent !important;
}

.markdown-body h1,
.markdown-body h2,
.markdown-body h3,
.markdown-body h4,
.markdown-body h5,
.markdown-body h6 {
  margin-top: 1.5em;
  margin-bottom: 1em;
  color: #111827;
  border-bottom: none;
}

.markdown-body p {
  margin-bottom: 1.2em;
  white-space: pre-wrap;
}

.markdown-body code {
  background-color: var(--color-surface-muted);
  padding: 0.2em 0.4em;
  border-radius: var(--radius-sm);
  font-family: 'Fira Code', monospace;
}

.markdown-body pre {
  background-color: var(--color-surface-muted);
  padding: 1em;
  border-radius: var(--radius-md);
  overflow-x: auto;
}

.markdown-body pre code {
  background-color: transparent;
  padding: 0;
}

.markdown-body blockquote {
  border-left: 4px solid var(--border-color);
  padding-left: 1em;
  color: var(--text-secondary);
  margin: 1em 0;
  background-color: transparent;
}

.markdown-body ul,
.markdown-body ol {
  padding-left: 2em;
  margin: 1em 0;
}

.markdown-body img {
  max-width: 100%;
  border-radius: var(--radius-md);
}

.markdown-body a {
  color: var(--color-brand-600);
  text-decoration: none;
}

.markdown-body a:hover {
  text-decoration: underline;
}

.markdown-body table {
  border-collapse: collapse;
  width: 100%;
  margin: 1em 0;
}

.markdown-body th,
.markdown-body td {
  border: 1px solid var(--border-color);
  padding: 0.5em 1em;
}

.markdown-body th {
  background-color: var(--background-secondary);
}

/* 移动端样式 */
@media (max-width: 768px) {
  .text-display {
    padding: 0;
    height: calc(100vh - 8rem);
  }

  .text-content {
    margin: 0.6rem 0.55rem 4rem;
    padding: 1rem;
    border-radius: 10px;
  }

  .markdown-body {
    font-size: 0.9375rem;
    line-height: 1.6;
  }

  .markdown-body h1 { font-size: 1.5rem; }
  .markdown-body h2 { font-size: 1.3rem; }
  .markdown-body h3 { font-size: 1.1rem; }
  .markdown-body h4 { font-size: 1rem; }
  .markdown-body h5 { font-size: 0.9rem; }
  .markdown-body h6 { font-size: 0.8rem; }

  .markdown-body p {
    font-size: 0.9375rem;
    margin: 0.75rem 0;
  }

  .markdown-body ul,
  .markdown-body ol {
    padding-left: 1.25rem;
  }

  .markdown-body li {
    margin: 0.25rem 0;
  }

  .markdown-body pre,
  .markdown-body code {
    font-size: 0.875rem;
  }
}

/* 滚动条样式 */
.text-content::-webkit-scrollbar {
  width: 8px;
}

.text-content::-webkit-scrollbar-track {
  background: transparent;
}

.text-content::-webkit-scrollbar-thumb {
  background: var(--color-border-strong);
  border-radius: var(--radius-sm);
}

.text-content::-webkit-scrollbar-thumb:hover {
  background: var(--ink-tertiary);
}

/* Firefox 滚动条样式 */
.text-content {
  scrollbar-width: thin;
  scrollbar-color: var(--color-border-strong) transparent;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.material-icons.spinning {
  animation: spin 1s linear infinite;
}
</style>
